import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_GLITCHTIP_DSN;
// Версия сборки. Проставляется в CI (VITE_RELEASE) и должна совпадать со
// значением --release при загрузке sourcemap в GlitchTip - иначе он не
// свяжет событие с картами и стек останется минифицированным.
const release = import.meta.env.VITE_RELEASE;

// Инициализация должна быть вызвана один раз при старте приложения,
// до первого рендера (см. src/index.jsx).
export function initErrorReporting() {
  if (!dsn) {
    // DSN не задан (например, при локальной разработке без .env) -
    // просто не отправляем ошибки никуда, но и не падаем из-за этого.
    return;
  }
  Sentry.init({
    dsn,
    release,
    // GlitchTip совместим с протоколом Sentry SDK "из коробки" -
    // достаточно указать DSN своего инстанса, остальной код не меняется.
    sendDefaultPii: false,
    // Штатный механизм самого Sentry SDK (allowUrls) для отсечения ошибок
    // из чужого кода: инъекции мобильного оператора, скрипты того
    // приложения, чей встроенный WebView открыл страницу, расширения
    // браузера. Признак - ошибка возникла НЕ в одном из файлов нашего
    // бандла (/static/js/...), а в "глобальном коде" самой страницы или
    // стороннего скрипта. Матчится по стеку ошибки, а не по URL страницы,
    // поэтому работает одинаково для newlk.ambar-sklad.ru и
    // dev.newlk.ambar-sklad.ru.
    allowUrls: [/^https?:\/\/([a-z0-9-]+\.)*ambar-sklad\.ru\/static\/js\//],
    beforeSend(event, hint) {
      const err = hint?.originalException;
      // Это тот же самый признак, по которому api.js (интерцептор axios)
      // распознаёт "протухшую сессию" и делает logout: data.error === 'access_denied'.
      // Если backend вернул именно этот код - это ожидаемый сценарий,
      // не отправляем событие, чтобы не засорять issues.
      //
      // Любой другой 401/403 (например, нет прав на конкретный эндпоинт,
      // но сессия валидна, и backend вернул другой код ошибки) НЕ подпадает
      // под этот фильтр и уйдёт в GlitchTip как обычно.
      const isExpiredSessionLogout = err?.response?.data?.error === 'access_denied';

      if (isExpiredSessionLogout) {
        return null;
      }

      // Сетевые сбои на стороне устройства пользователя (status_code 0):
      // оборвалась мобильная связь, ушёл в туннель, закрыл вкладку во время
      // запроса. Это не ошибки приложения, и починить их кодом нельзя -
      // а на телефонах они происходят постоянно и заглушают настоящие issues.
      //
      // ВАЖНО: с точки зрения axios у ERR_NETWORK без response ДВЕ разные
      // причины, неразличимые по коду ошибки - браузер намеренно не даёт
      // JS увидеть, какая именно:
      //   1) реальный обрыв связи на устройстве;
      //   2) запрос заблокирован CORS-политикой (неверный
      //      Access-Control-Allow-Origin/Credentials на бэкенде/nginx) -
      //      это НАСТОЯЩАЯ ошибка приложения/инфраструктуры, а не шум,
      //      и её обязательно нужно видеть в трекере.
      // Различаем через navigator.onLine: если ОС считает устройство
      // онлайн, а запрос всё равно упал без ответа - это не про связь
      // пользователя, значит не фильтруем.
      const isDeviceNetworkIssue =
        err?.isAxiosError &&
        !err?.response &&
        !navigator.onLine &&
        ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED'].includes(err?.code);

      if (isDeviceNetworkIssue) {
        return null;
      }

      return event;
    },
  });
}

// Единая точка отправки ошибок. Используется из ErrorBoundary и может
// использоваться из любого другого места (например, catch-блоков в
// thunk'ах), где хочется явно залогировать ошибку.
export function reportError(error, extra = {}) {
  console.error('[reportError]', error, extra);

  if (dsn) {
    Sentry.captureException(error, { extra });
  }
}
